import React, { useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, Send, Upload, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { CAREER_TYPES, CAREER_AREAS } from "../../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 5;
const EXT_OK = /\.(pdf|doc|docx)$/i;

const empty = {
  full_name: "", email: "", phone: "", city: "", application_type: "", area_of_interest: "", qualification: "",
  college: "", graduation_year: "", current_company: "", job_title: "", experience: "", skills: "",
  linkedin_url: "", portfolio_url: "", message: "", consent: false, website: "",
};

const inputCls =
  "w-full bg-white/[0.04] border rounded-xl px-4 py-3 text-white placeholder:text-slate-500 text-sm transition-colors duration-200 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange";
const selectCls = `${inputCls} appearance-none bg-navy-deep/40 [&>option]:bg-[#0A1F44]`;

const Field = ({ error, children }) => (
  <div>
    {children}
    {error && <p className="text-red-400 text-xs mt-1.5">{error}</p>}
  </div>
);

export const CareerForm = ({ opening = null, onClearOpening }) => {
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const fileRef = useRef(null);

  const type = CAREER_TYPES.find((t) => t.id === form.application_type);
  const isStudent = form.application_type === "student";
  const isFresher = form.application_type === "fresher";
  const isPro = form.application_type === "experienced";

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!EXT_OK.test(f.name)) return setErrors((er) => ({ ...er, resume: "Please upload a PDF, DOC or DOCX file" }));
    if (f.size > MAX_MB * 1024 * 1024) return setErrors((er) => ({ ...er, resume: `Resume must be ${MAX_MB} MB or smaller` }));
    setFile(f);
    setErrors((er) => ({ ...er, resume: undefined }));
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Please enter your phone number";
    if (!form.city.trim()) e.city = "Please enter your current city";
    if (!form.application_type) e.application_type = "Please select how you are applying";
    if (!form.area_of_interest) e.area_of_interest = "Please select an area of interest";
    if (!form.qualification.trim()) e.qualification = isStudent ? "Please enter your degree / qualification" : "Please enter your highest qualification";
    if (!form.skills.trim()) e.skills = "Please list your key skills";
    if (isPro && !file) e.resume = "A resume is required for experienced professionals";
    if (!form.consent) e.consent = "Please provide your consent to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, typeof v === "boolean" ? String(v) : v));
    if (opening) fd.append("opening_id", opening.id);
    if (file) fd.append("resume", file);
    try {
      await axios.post(`${API}/careers/apply`, fd);
      setStatus("success");
      setForm(empty);
      clearFile();
      onClearOpening?.();
      toast.success("Profile submitted — thank you.");
    } catch (err) {
      setStatus("idle");
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object") {
        setErrors(d);
        toast.error(d.form || "Please review the highlighted fields.");
      } else toast.error("Something went wrong. Please try again in a moment.");
    }
  };

  const border = (k) => (errors[k] ? "border-red-400" : "border-white/10");

  return (
    <AnimatePresence mode="wait">
      {status === "success" ? (
        <motion.div key="ok" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} data-testid="careers-success" className="glass rounded-2xl p-10 text-center flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-brand-orange/15 grid place-items-center"><CheckCircle2 className="text-brand-orange" size={34} /></div>
          <h3 className="font-head text-2xl font-semibold text-white">Thank you for your interest in ATS IT Solutions</h3>
          <p className="text-slate-400 text-sm max-w-md">Your profile has been submitted successfully. Our team will review your information and may contact you if a suitable opportunity matches your profile.</p>
          <button onClick={() => setStatus("idle")} data-testid="careers-submit-another" className="btn-secondary mt-2">Submit another profile</button>
        </motion.div>
      ) : (
        <motion.form key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onSubmit={submit} data-testid="careers-form" className="space-y-8" noValidate>
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />

          {opening && (
            <div data-testid="career-applying-for" className="flex items-center justify-between gap-3 rounded-xl border border-brand-orange/40 bg-brand-orange/10 px-4 py-3 text-sm">
              <span className="text-slate-200 min-w-0"><span className="font-mono text-[10px] tracking-wider uppercase text-brand-orange block">Applying for</span><span className="truncate block">{opening.title}</span></span>
              <button type="button" onClick={onClearOpening} data-testid="career-applying-for-clear" className="text-slate-400 hover:text-white text-xs shrink-0">General profile instead</button>
            </div>
          )}

          <fieldset className="space-y-4">
            <legend className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">Application</legend>
            <Field error={errors.application_type}>
              <label className="block text-sm text-slate-300 mb-2" htmlFor="career-type">I am applying as *</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" id="career-type">
                {CAREER_TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    role="radio"
                    aria-checked={form.application_type === t.id}
                    data-testid={`career-type-${t.id}`}
                    onClick={() => { setForm((f) => ({ ...f, application_type: t.id })); setErrors((er) => ({ ...er, application_type: undefined, resume: undefined })); }}
                    className={`rounded-xl px-4 py-3 text-sm text-left border transition-colors duration-200 ${form.application_type === t.id ? "bg-brand-orange/15 border-brand-orange/60 text-white" : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>
            <Field error={errors.area_of_interest}>
              <select data-testid="career-area" value={form.area_of_interest} onChange={set("area_of_interest")} className={`${selectCls} ${border("area_of_interest")} ${form.area_of_interest ? "text-white" : "text-slate-500"}`}>
                <option value="">Area of Interest *</option>
                {CAREER_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">Personal information</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field error={errors.full_name}><input data-testid="career-name" placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} className={`${inputCls} ${border("full_name")}`} /></Field>
              <Field error={errors.email}><input data-testid="career-email" type="email" placeholder="Email Address *" value={form.email} onChange={set("email")} className={`${inputCls} ${border("email")}`} /></Field>
              <Field error={errors.phone}><input data-testid="career-phone" type="tel" placeholder="Phone Number *" value={form.phone} onChange={set("phone")} className={`${inputCls} ${border("phone")}`} /></Field>
              <Field error={errors.city}><input data-testid="career-city" placeholder="Current City / Location *" value={form.city} onChange={set("city")} className={`${inputCls} ${border("city")}`} /></Field>
            </div>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">{isPro ? "Education & professional information" : "Education information"}</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field error={errors.qualification}><input data-testid="career-qualification" placeholder={isStudent ? "Degree / Qualification (e.g. B.Tech CSE, pursuing) *" : "Highest Qualification *"} value={form.qualification} onChange={set("qualification")} className={`${inputCls} ${border("qualification")}`} /></Field>
              {!isPro && <Field error={errors.college}><input data-testid="career-college" placeholder="College / University" value={form.college} onChange={set("college")} className={`${inputCls} ${border("college")}`} /></Field>}
              {!isPro && <Field error={errors.graduation_year}><input data-testid="career-year" inputMode="numeric" maxLength={4} placeholder={isStudent ? "Expected Graduation Year" : "Passing Year"} value={form.graduation_year} onChange={set("graduation_year")} className={`${inputCls} ${border("graduation_year")}`} /></Field>}
              {isPro && <Field error={errors.current_company}><input data-testid="career-company" placeholder="Current Company" value={form.current_company} onChange={set("current_company")} className={`${inputCls} ${border("current_company")}`} /></Field>}
              {isPro && <Field error={errors.job_title}><input data-testid="career-title" placeholder="Current Job Title" value={form.job_title} onChange={set("job_title")} className={`${inputCls} ${border("job_title")}`} /></Field>}
              {isPro && <Field error={errors.experience}><input data-testid="career-experience" placeholder="Total Work Experience (e.g. 4 years)" value={form.experience} onChange={set("experience")} className={`${inputCls} ${border("experience")}`} /></Field>}
            </div>
            <Field error={errors.skills}><input data-testid="career-skills" placeholder={isPro ? "Key Skills (comma separated) *" : "Current Skills (comma separated) *"} value={form.skills} onChange={set("skills")} className={`${inputCls} ${border("skills")}`} /></Field>
          </fieldset>

          <fieldset className="space-y-4">
            <legend className="font-mono text-xs tracking-wider uppercase text-slate-500 mb-4">Online profile &amp; resume</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field error={errors.linkedin_url}><input data-testid="career-linkedin" placeholder="LinkedIn Profile URL (optional)" value={form.linkedin_url} onChange={set("linkedin_url")} className={`${inputCls} ${border("linkedin_url")}`} /></Field>
              <Field error={errors.portfolio_url}><input data-testid="career-portfolio" placeholder="Portfolio / GitHub URL (optional)" value={form.portfolio_url} onChange={set("portfolio_url")} className={`${inputCls} ${border("portfolio_url")}`} /></Field>
            </div>
            <Field error={errors.resume}>
              <label data-testid="career-resume-label" className="block text-sm text-slate-300 mb-2">
                {type ? type.resumeLabel : "Upload Resume"}
                {isFresher && <span className="text-slate-500"> — a resume helps us assess your profile, but you can submit without one.</span>}
                {isStudent && <span className="text-slate-500"> — students may submit without a resume; we'll review the details you share here.</span>}
              </label>
              <input ref={fileRef} id="career-resume" data-testid="career-resume" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={onFile} className="sr-only" />
              {file ? (
                <div data-testid="career-resume-selected" className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${border("resume")} bg-white/[0.04]`}>
                  <span className="flex items-center gap-2 text-slate-200 min-w-0"><FileText size={16} className="text-brand-orange shrink-0" /><span className="truncate">{file.name}</span><span className="text-slate-500 shrink-0">({Math.max(1, Math.round(file.size / 1024))} KB)</span></span>
                  <button type="button" onClick={clearFile} aria-label="Remove file" data-testid="career-resume-remove" className="text-slate-400 hover:text-white p-1"><X size={16} /></button>
                </div>
              ) : (
                <label htmlFor="career-resume" data-testid="career-resume-dropzone" className={`flex items-center gap-3 rounded-xl border border-dashed px-4 py-4 text-sm cursor-pointer transition-colors hover:bg-white/[0.04] ${border("resume")}`}>
                  <span className="w-10 h-10 rounded-lg bg-brand-orange/15 grid place-items-center shrink-0"><Upload size={18} className="text-brand-orange" /></span>
                  <span className="text-slate-300">Choose a file <span className="text-slate-500">— PDF, DOC or DOCX, up to {MAX_MB} MB</span></span>
                </label>
              )}
            </Field>
          </fieldset>

          <Field error={errors.message}>
            <textarea data-testid="career-message" rows={4} placeholder="Why would you like to explore an opportunity with ATS IT Solutions? (optional)" value={form.message} onChange={set("message")} className={`${inputCls} ${border("message")} resize-none`} />
          </Field>

          <Field error={errors.consent}>
            <label className={`flex items-start gap-3 rounded-xl border p-4 text-sm text-slate-300 cursor-pointer ${border("consent")}`}>
              <input data-testid="career-consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 shrink-0 accent-[#F26522]" />
              <span>
                I consent to ATS IT Solutions collecting and storing my profile, contact information and resume, where provided, for recruitment purposes and for consideration for relevant current and future career opportunities. See our{" "}
                <Link to="/privacy" data-testid="career-privacy-link" className="text-brand-orange hover:underline">Privacy Policy</Link>. *
              </span>
            </label>
          </Field>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <button type="submit" disabled={status === "loading"} data-testid="career-submit" className="btn-primary disabled:opacity-60">
              {status === "loading" ? "Submitting…" : "Submit Your Profile"} <Send size={16} />
            </button>
            <p className="text-xs text-slate-500">Submitting a profile does not guarantee an interview, internship or job. Your details are used for recruitment purposes only.</p>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
};

export default CareerForm;
